import {
  fetchEntityByMurn,
  formatParentEntityLabel,
  getEntityViewLocation,
} from "@/services/entityMurnService";
import { useElectronicFormStore } from "@/views/electronicForm/electronicFormStore";
import type { ToastOptionsError } from "@modernary/mui/components/toasts";
import { computed, ref, watch } from "vue";
import { useRoute, type LocationQuery } from "vue-router";

/**
 * Ownership-scoped reads answer 404 rather than 403 so they cannot confirm a record exists,
 * so a parent the user may not read arrives as either status. The subheading renders nothing
 * when the fetch fails, so neither is worth a toast.
 */
const hideUnreadableParent = () => ({
  errorToastCb: (options: ToastOptionsError) => {
    options.hideStatusCodes = [403, 404];
  },
});

function firstQueryString(value: unknown): string | undefined {
  const raw = Array.isArray(value) ? value[0] : value;
  if (raw == null || raw === "") return undefined;
  return String(raw);
}

export function getParentEntityMurnFromQuery(query: LocationQuery): string | undefined {
  return firstQueryString(query.parentEntityMurn);
}

/** Parent entity link for LayoutEntity #subheading / #subheadingNew. */
export function useParentEntitySubheading() {
  const route = useRoute();
  const electronicFormStore = useElectronicFormStore();
  const parentLabel = ref("");

  const parentEntityMurn = computed(
    () =>
      electronicFormStore.electronicForm?.parentEntityMurn ||
      getParentEntityMurnFromQuery(route.query) ||
      undefined,
  );

  const parentViewLocation = computed(() => getEntityViewLocation(parentEntityMurn.value));

  watch(
    parentEntityMurn,
    async (murn) => {
      parentLabel.value = "";
      if (!murn) return;
      try {
        const response = await fetchEntityByMurn(murn, hideUnreadableParent());
        parentLabel.value = formatParentEntityLabel(murn, response?.data);
      } catch {
        parentLabel.value = "";
      }
    },
    { immediate: true },
  );

  return { parentEntityMurn, parentViewLocation, parentLabel };
}
